import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { DataSourceStatusGlyph } from "./DataSourceStatusGlyph.js";
import { SourceSystemLogoByName } from "src/features/integrations/overview/table/SourceSystemLogo.js";
import { useDataSourcesBasePath } from "src/features/integrations/useDataSourcesBasePath.js";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";
import type { DataSourceStatusRow } from "./summarizeIntegrationStatus.js";

interface IntegrationStatusWidgetViewProps {
	isLoading: boolean;
	isError: boolean;
	rows: DataSourceStatusRow[] | undefined;
	action?: ReactNode;
}

export const IntegrationStatusWidgetView = ({
	isLoading,
	isError,
	rows,
	action,
}: IntegrationStatusWidgetViewProps) => {
	const { t } = useTranslation();
	const dataSourcesBase = useDataSourcesBasePath();

	return (
		<DashboardWidgetBase title={t("Status")} action={action} flush>
			{isLoading ? (
				<p className="p-6 text-sm text-grey-500">{t("Loading")}</p>
			) : isError || !rows ? (
				<p className="p-6 text-sm text-gray-600">
					{t("Could not load integration status.")}
				</p>
			) : rows.length === 0 ? (
				<p className="p-6 text-sm text-gray-600">
					{t("No data sources configured yet.")}
				</p>
			) : (
				<ul className="@container">
					{rows.map(({ id, dataSource }) => {
						// Same rule as the data-sources table row: the SIE upload flow
						// has its own detail page.
						const href =
							dataSource.fusionHubIntegration.type === "SIE"
								? `${dataSourcesBase}/sie/${dataSource.id}`
								: `${dataSourcesBase}/${dataSource.id}`;
						const rowClassName =
							"flex items-center gap-3 px-6 py-3 hover:bg-grey-50";
						const content = (
							<>
								<SourceSystemLogoByName
									sourceSystemName={dataSource.sourceSystem}
									size="small"
								/>
								<span className="min-w-0 grow truncate text-sm font-semibold text-grey-900">
									{dataSource.name}
								</span>
								<DataSourceStatusGlyph dataSource={dataSource} />
							</>
						);
						return (
							<li key={id} className="border-b border-grey-200 last:border-b-0">
								<Link to={href} viewTransition className={rowClassName}>
									{content}
								</Link>
							</li>
						);
					})}
				</ul>
			)}
		</DashboardWidgetBase>
	);
};
